<template>
  <AppShell>
    <section class="space-y-8">
      <div class="space-y-3">
        <p class="app-eyebrow">{{ t("kycDocumentUpload.eyebrow") }}</p>
        <h1 class="text-3xl font-bold tracking-tight">{{ t("kycDocumentUpload.title") }}</h1>
        <p class="max-w-2xl text-sm leading-7 text-brand-muted-strong">
          {{ t("kycDocumentUpload.description") }}
        </p>
      </div>

      <Card class="rounded-none border-brand-line bg-brand-surface shadow-none">
        <CardContent class="space-y-6 p-8">
          <label class="block space-y-3">
            <span class="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-muted">
              {{ t("kycDocumentUpload.fileLabel") }}
            </span>
            <Input type="file" accept="application/pdf,.pdf" class="h-12 rounded-none" @change="selectFile" />
          </label>

          <div v-if="selectedFileName" class="border border-brand-line p-4 text-sm">
            <p class="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-muted">
              {{ t("kycDocumentUpload.selectedFile") }}
            </p>
            <p class="mt-1 break-words font-semibold text-brand-ink">{{ selectedFileName }}</p>
          </div>

          <p v-if="uploadStatus" class="text-sm font-semibold text-brand-ink">
            {{ uploadStatus }}
          </p>
          <p v-if="uploadError" class="text-sm font-medium text-destructive">
            {{ uploadError }}
          </p>

          <div class="flex flex-col gap-3 md:flex-row">
            <Button
              class="h-12 rounded-none border-0 bg-brand-fill px-8 text-[12px] font-bold uppercase tracking-[0.14em] text-white shadow-none hover:bg-brand-fill-hover"
              :disabled="!selectedFile || uploading"
              type="button"
              @click="uploadDocument"
            >
              <Upload class="size-4" />
              {{ uploading ? t("kycDocumentUpload.uploading") : t("kycDocumentUpload.upload") }}
            </Button>
            <Button
              variant="outline"
              class="h-12 rounded-none px-8 text-[12px] font-bold uppercase tracking-[0.14em]"
              type="button"
              @click="backToKyc"
            >
              {{ t("kycDocumentUpload.back") }}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  </AppShell>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Upload } from "lucide-vue-next";
import { createOrvalRequestOptions, uploadPortalKycPoa } from "@token/shared";
import AppShell from "@/components/AppShell.vue";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useApi } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const router = useRouter();
const route = useRoute();
const api = useApi();
const { t } = useI18n();
const selectedFileName = ref("");
const selectedFile = ref<File | null>(null);
const uploading = ref(false);
const uploadError = ref("");
const uploadStatus = ref("");

function selectFile(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  const isPdf = file?.type === "application/pdf" || Boolean(file?.name.toLowerCase().endsWith(".pdf"));
  selectedFile.value = file && isPdf ? file : null;
  selectedFileName.value = selectedFile.value?.name ?? "";
  uploadError.value = file && !isPdf ? t("kycDocumentUpload.invalidFile") : "";
  uploadStatus.value = "";
}

async function uploadDocument() {
  const kycId = routeQueryString(route.query.kycId);
  if (!selectedFile.value || !kycId) {
    uploadError.value = t("kycDocumentUpload.missingKyc");
    return;
  }

  uploading.value = true;
  uploadError.value = "";
  uploadStatus.value = "";

  try {
    await uploadPortalKycPoa({ kycId }, { file: selectedFile.value }, createOrvalRequestOptions(api));
    uploadStatus.value = t("kycDocumentUpload.uploadSuccess");
  } catch (error) {
    uploadError.value = error instanceof Error ? error.message : t("kycDocumentUpload.uploadError");
  } finally {
    uploading.value = false;
  }
}

function routeQueryString(value: unknown): string | undefined {
  if (typeof value === "string" && value.length > 0) return value;
  if (Array.isArray(value)) return routeQueryString(value[0]);
  return undefined;
}

function backToKyc() {
  router.push({ name: "kyc-status" });
}
</script>
